import React, { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import {
  AlertTriangle, ArrowRight, Ban, Car, CheckCircle, Clock, Eye, RefreshCw, Search, X, XCircle,
} from 'lucide-react';
import { api } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

/*
    Drivers asking to drive a different one of their approved vehicles (app: My
    Vehicles → Switch to this vehicle). Approve switches the driver's active vehicle
    immediately; Reject keeps the current one and the driver sees the reason.
*/

const TABS = [
  { key: 'PENDING', label: 'Pending' },
  { key: 'APPROVED', label: 'Approved' },
  { key: 'REJECTED', label: 'Rejected' },
  { key: 'CANCELLED', label: 'Cancelled' },
];

const REJECT_REASONS = [
  'Vehicle documents expired',
  'Insurance is not valid',
  'Permit is not valid for this vehicle',
  'Vehicle needs inspection first',
  'Driver not eligible for this vehicle type',
  'Other',
];

const STATUS_STYLE = {
  PENDING: { badge: 'badge-warning', box: 'bg-yellow-50 text-yellow-800', icon: Clock, iconColor: 'text-yellow-600', text: 'Waiting for approval' },
  APPROVED: { badge: 'badge-success', box: 'bg-green-50 text-green-800', icon: CheckCircle, iconColor: 'text-green-600', text: 'Approved — driver switched' },
  REJECTED: { badge: 'badge-error', box: 'bg-red-50 text-red-800', icon: XCircle, iconColor: 'text-red-600', text: 'Rejected — driver kept the current vehicle' },
  CANCELLED: { badge: 'badge-secondary', box: 'bg-gray-50 text-gray-700', icon: Ban, iconColor: 'text-gray-500', text: 'Cancelled' },
};

const typeLabel = type => (type ? String(type).replace(/_/g, ' ') : '—');
const when = ts => (ts ? new Date(ts).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—');
// Same rules and colours as the Vehicles list's Expiry Dates column.
const formatExpiryDate = timestamp => {
  if (!timestamp) return 'N/A';
  const ms = timestamp < 1e12 ? timestamp * 1000 : timestamp;
  const date = new Date(ms);
  const daysUntilExpiry = Math.ceil((date - new Date()) / (1000 * 60 * 60 * 24));
  if (daysUntilExpiry < 0) return <span className="text-red-600 font-medium">Expired {Math.abs(daysUntilExpiry)} days ago</span>;
  if (daysUntilExpiry <= 30) return <span className="text-yellow-600 font-medium">Expires in {daysUntilExpiry} days</span>;
  return <span className="text-green-600">{date.toLocaleDateString()}</span>;
};

const ExpiryDates = ({ expiry, boxed }) => (
  <div className={boxed ? 'p-3 bg-gray-50 rounded-lg space-y-1 text-sm' : 'space-y-1 text-sm'}>
    {boxed ? <p className="text-xs text-gray-500 mb-1">Requested vehicle · expiry dates</p> : null}
    <div><span className="text-gray-500">Insurance:</span> {formatExpiryDate(expiry?.insuranceExpiry)}</div>
    <div><span className="text-gray-500">Fitness:</span> {formatExpiryDate(expiry?.fitnessExpiry)}</div>
    <div><span className="text-gray-500">PUC:</span> {formatExpiryDate(expiry?.pucExpiry)}</div>
  </div>
);

const cancelledText = r => (r.cancelReason === 'replaced_by_new_request' ? 'Replaced by a newer request' : 'Cancelled by the driver');

const VehicleChip = ({ regNo, type, highlight }) => (
  <div className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${highlight ? 'border-indigo-300 bg-indigo-50' : 'border-gray-200 bg-white'}`}>
    <Car className={`h-4 w-4 ${highlight ? 'text-indigo-600' : 'text-gray-500'}`} />
    <div>
      <div className="text-sm font-semibold text-gray-900">{regNo || '—'}</div>
      <div className="text-xs text-gray-500">{typeLabel(type)}</div>
    </div>
  </div>
);

const ModalShell = ({ title, onClose, children }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
      </div>
      {children}
    </div>
  </div>
);

const SwitchSummary = ({ r }) => (
  <div className="p-3 bg-gray-50 rounded-lg space-y-3">
    <div>
      <p className="text-xs text-gray-500">Driver</p>
      <p className="text-sm font-medium text-gray-900">{r.driverName || '—'} <span className="text-gray-500 font-normal">· {r.driverPhone || r.driverId}</span></p>
    </div>
    <div className="flex items-center gap-3">
      <VehicleChip regNo={r.fromRegNo} type={r.fromType} />
      <ArrowRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
      <VehicleChip regNo={r.toRegNo} type={r.toType} highlight />
    </div>
  </div>
);

// Same look as the vehicle Unblock (activate) modal.
const ApproveModal = ({ request, onClose, onDone }) => {
  const [saving, setSaving] = useState(false);
  const approve = async () => {
    setSaving(true);
    try {
      const res = await api.patch(`/vehicle-switch-requests/${request._id}/approve`);
      toast.success(res?.data?.message || 'Vehicle switched');
      onDone();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to approve request');
    } finally {
      setSaving(false);
    }
  };
  return (
    <ModalShell title="Approve Vehicle Switch" onClose={onClose}>
      <div className="space-y-4">
        <div className="flex items-center p-3 bg-green-50 rounded-lg">
          <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0" />
          <span className="text-sm text-green-800">
            The driver's active vehicle changes to <strong>{request.toRegNo}</strong> immediately. New ride requests and fares will be for this vehicle.
          </span>
        </div>
        <SwitchSummary r={request} />
        <ExpiryDates expiry={request.toVehicleExpiry} boxed />
        <p className="text-xs text-gray-500">Requested {when(request.requestedAt)}</p>
        <button
          onClick={approve}
          disabled={saving}
          className="w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 disabled:opacity-50">
          {saving ? 'Switching...' : 'Approve & Switch Vehicle'}
        </button>
      </div>
    </ModalShell>
  );
};

// Same look as the vehicle Block modal: a reason is required.
const RejectModal = ({ request, onClose, onDone }) => {
  const [choice, setChoice] = useState('');
  const [other, setOther] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const reason = choice === 'Other' ? other.trim() : choice;

  const reject = async e => {
    e.preventDefault();
    if (!choice) { setError('Select a reason'); toast.error('Select a reason for rejecting'); return; }
    if (choice === 'Other' && !other.trim()) { setError('Enter the reason'); toast.error('Enter the reason for rejecting'); return; }
    setSaving(true);
    try {
      await api.patch(`/vehicle-switch-requests/${request._id}/reject`, { reason });
      toast.success('Switch request rejected');
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to reject request');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell title="Reject Vehicle Switch" onClose={onClose}>
      <form onSubmit={reject} className="space-y-4">
        <div className="flex items-center p-3 bg-yellow-50 rounded-lg">
          <AlertTriangle className="h-5 w-5 text-yellow-600 mr-2 flex-shrink-0" />
          <span className="text-sm text-yellow-800">The driver keeps driving {request.fromRegNo || 'the current vehicle'} and sees this reason in the app.</span>
        </div>
        <SwitchSummary r={request} />
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Reason for Rejecting <span className="text-red-500">*</span></label>
          <select
            value={choice}
            onChange={e => { setChoice(e.target.value); setError(''); }}
            className={`w-full border rounded-lg px-3 py-2 ${error && !choice ? 'border-red-400' : 'border-gray-300'}`}>
            <option value="">Select a reason</option>
            {REJECT_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        {choice === 'Other' ? (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reason <span className="text-red-500">*</span></label>
            <textarea
              value={other}
              onChange={e => { setOther(e.target.value); setError(''); }}
              rows={3}
              maxLength={300}
              placeholder="Write the reason the driver will see"
              className={`w-full border rounded-lg px-3 py-2 ${error && !other.trim() ? 'border-red-400' : 'border-gray-300'}`}
              autoFocus
            />
          </div>
        ) : null}
        {error ? <p className="text-sm text-red-600">{error}</p> : null}
        <button
          type="submit"
          disabled={saving}
          className="w-full bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 disabled:opacity-50">
          {saving ? 'Rejecting...' : 'Reject Request'}
        </button>
      </form>
    </ModalShell>
  );
};

const DetailsModal = ({ request, onClose }) => {
  const st = STATUS_STYLE[request.status] || STATUS_STYLE.PENDING;
  const StatusIcon = st.icon;
  return (
    <ModalShell title="Switch Request Details" onClose={onClose}>
      <div className="space-y-4">
        <div className={`flex items-center p-3 rounded-lg ${st.box}`}>
          <StatusIcon className={`h-5 w-5 mr-2 flex-shrink-0 ${st.iconColor}`} />
          <span className="text-sm font-medium">{request.status === 'CANCELLED' ? cancelledText(request) : st.text}</span>
        </div>
        <SwitchSummary r={request} />
        <ExpiryDates expiry={request.toVehicleExpiry} boxed />
        {request.status === 'REJECTED' ? (
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600"><strong>Reason:</strong> {request.rejectReason || '—'}</p>
          </div>
        ) : null}
        <div className="p-3 bg-gray-50 rounded-lg space-y-1">
          <p className="text-sm text-gray-600"><strong>Requested:</strong> {when(request.requestedAt)}</p>
          {request.status !== 'PENDING' ? (
            <p className="text-sm text-gray-600">
              <strong>{request.status === 'CANCELLED' ? 'Cancelled' : 'Decided'}:</strong> {when(request.decidedAt)}
              {request.decidedByName ? ` · by ${request.decidedByName}` : ''}
            </p>
          ) : null}
        </div>
      </div>
    </ModalShell>
  );
};

const VehicleSwitchRequests = () => {
  const { user } = useAuth();
  const canWrite = !!user?.permissions?.vehicles?.write;
  const [status, setStatus] = useState('PENDING');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState(null); // { kind: 'approve' | 'reject' | 'details', request }
  const queryClient = useQueryClient();

  const { data, isLoading, isFetching, refetch } = useQuery(
    ['vehicle-switch-requests', status, search, page],
    async () => (await api.get('/vehicle-switch-requests', { params: { status, search: search || undefined, page, limit: 20 } })).data,
    { keepPreviousData: true, refetchInterval: status === 'PENDING' ? 30000 : false },
  );

  const rows = data?.data || [];
  const counts = data?.counts || {};
  const pages = data?.pagination?.pages || 1;
  const done = () => {
    setModal(null);
    refetch();
    queryClient.invalidateQueries(['header-switch-requests']); // header bell count
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Vehicle Switch Requests</h1>
          <p className="text-sm text-gray-500 mt-1">
            Drivers asking to drive another of their approved vehicles. Approving switches their vehicle immediately.
          </p>
        </div>
        <button className="btn btn-outline" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex gap-2">
              {TABS.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => { setStatus(tab.key); setPage(1); }}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium border ${status === tab.key ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'}`}>
                  {tab.label}
                  {counts[tab.key] ? <span className={`ml-2 text-xs ${status === tab.key ? 'text-indigo-100' : 'text-gray-400'}`}>{counts[tab.key]}</span> : null}
                </button>
              ))}
            </div>
            <div className="relative">
              <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                className="input pl-9"
                placeholder="Driver, phone or reg. number"
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
              />
            </div>
          </div>
        </div>

        <div className="card-body">
          {isLoading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading requests...</p>
            </div>
          ) : rows.length === 0 ? (
            <div className="empty-state">
              <Car className="empty-state-icon" />
              <h3 className="empty-state-title">No {status.toLowerCase()} requests</h3>
              <p className="empty-state-description">
                {status === 'PENDING' ? 'New requests from drivers will appear here.' : 'Nothing here yet.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">Driver</th>
                    <th className="table-header-cell">Switch</th>
                    <th className="table-header-cell">Expiry Dates</th>
                    <th className="table-header-cell">{status === 'PENDING' ? 'Requested' : 'Decided'}</th>
                    <th className="table-header-cell">Status</th>
                    <th className="table-header-cell">Actions</th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {rows.map(r => {
                    const st = STATUS_STYLE[r.status] || STATUS_STYLE.PENDING;
                    return (
                      <tr key={r._id} className="table-row">
                        <td className="table-cell">
                          <div className="font-medium text-gray-900">{r.driverName || '—'}</div>
                          <div className="text-xs text-gray-500">{r.driverPhone || r.driverId}</div>
                        </td>
                        <td className="table-cell">
                          <div className="flex items-center gap-3">
                            <VehicleChip regNo={r.fromRegNo} type={r.fromType} />
                            <ArrowRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                            <VehicleChip regNo={r.toRegNo} type={r.toType} highlight />
                          </div>
                        </td>
                        <td className="table-cell">
                          <ExpiryDates expiry={r.toVehicleExpiry} />
                        </td>
                        <td className="table-cell text-sm text-gray-600">
                          <div className="flex items-center gap-1">
                            <Clock className="h-3.5 w-3.5 text-gray-400" />
                            {when(status === 'PENDING' ? r.requestedAt : r.decidedAt)}
                          </div>
                          {r.decidedByName ? <div className="text-xs text-gray-400 mt-1">by {r.decidedByName}</div> : null}
                        </td>
                        <td className="table-cell">
                          <span className={`badge ${st.badge}`}>
                            {r.status === 'PENDING' ? 'Pending' : r.status === 'APPROVED' ? 'Switched' : r.status === 'REJECTED' ? 'Rejected' : 'Cancelled'}
                          </span>
                          {r.status === 'REJECTED' && r.rejectReason ? (
                            <div className="text-xs text-gray-500 mt-1 max-w-xs truncate" title={r.rejectReason}>{r.rejectReason}</div>
                          ) : null}
                          {r.status === 'CANCELLED' ? <div className="text-xs text-gray-500 mt-1">{cancelledText(r)}</div> : null}
                        </td>
                        <td className="table-cell">
                          <div className="flex gap-2">
                            {status === 'PENDING' && canWrite ? (
                              <>
                                <button className="btn btn-sm btn-success" onClick={() => setModal({ kind: 'approve', request: r })}>
                                  <CheckCircle className="h-4 w-4 mr-1" />
                                  Approve
                                </button>
                                <button className="btn btn-sm btn-danger" onClick={() => setModal({ kind: 'reject', request: r })}>
                                  <XCircle className="h-4 w-4 mr-1" />
                                  Reject
                                </button>
                              </>
                            ) : null}
                            <button className="btn btn-sm btn-outline" onClick={() => setModal({ kind: 'details', request: r })} title="View details">
                              <Eye className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {pages > 1 ? (
            <div className="flex items-center justify-end gap-2 mt-4">
              <button className="btn btn-sm btn-outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</button>
              <span className="text-sm text-gray-600">Page {page} of {pages}</span>
              <button className="btn btn-sm btn-outline" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>Next</button>
            </div>
          ) : null}
        </div>
      </div>

      {modal?.kind === 'approve' ? <ApproveModal request={modal.request} onClose={() => setModal(null)} onDone={done} /> : null}
      {modal?.kind === 'reject' ? <RejectModal request={modal.request} onClose={() => setModal(null)} onDone={done} /> : null}
      {modal?.kind === 'details' ? <DetailsModal request={modal.request} onClose={() => setModal(null)} /> : null}
    </div>
  );
};

export default VehicleSwitchRequests;
